import React from "react";

import { COPY } from "./copy";
import { SidePanel } from "./SidePanel";
import { MOCK_PROPERTY_ACCOUNTS, findPropertiesByIds } from "./fixtures/oauth-demo";
import { SYNC_INTERVAL_HOURS, SyncStatus, formatDate, formatDateTime } from "./fixtures/sync-demo";
import { SetupState, currencySymbol } from "./setupState";

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
    return (
        <section className="border-b border-gray-100 pb-5 last:border-b-0 last:pb-0">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{heading}</h3>
            <div className="mt-2.5 space-y-1.5">{children}</div>
        </section>
    );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-4">
            <span className="flex-none text-[12px] text-gray-500">{label}</span>
            <span className="min-w-0 break-words text-right text-[12.5px] text-gray-900">{value}</span>
        </div>
    );
}

/** Which GA4 account a property belongs to — shown so support can find it fast. */
function accountNameFor(propertyId: string): string {
    const owner = MOCK_PROPERTY_ACCOUNTS.find((a) => a.properties.some((p) => p.id === propertyId));
    return owner ? `${owner.accountName} · ${owner.accountId}` : "—";
}

/**
 * The permanent home for the connection details, above all the Property IDs.
 * After setup there is nowhere else a customer can read back the number support
 * asks them for, so this panel stays reachable from the report header.
 */
export function ConnectionSettingsPanel({
    open,
    onClose,
    setup,
    syncStatus,
    onChangeProperties,
    onDisconnect
}: {
    open: boolean;
    onClose: () => void;
    setup: SetupState;
    syncStatus: SyncStatus;
    onChangeProperties: () => void;
    onDisconnect: () => void;
}) {
    const c = COPY.connection;
    const properties = findPropertiesByIds(setup.propertyIds);
    const symbol = currencySymbol(setup.currency);

    return (
        <SidePanel open={open} title={c.title} onClose={onClose}>
            {!setup.connected || properties.length === 0 ? (
                <p className="text-[12.5px] text-gray-500">{c.notConnected}</p>
            ) : (
                <div className="space-y-5">
                    <Section heading={c.account.heading}>
                        <Row label="Email" value={setup.googleAccountEmail ?? "—"} />
                        <Row label="Access" value={c.account.scope} />
                        <button
                            type="button"
                            onClick={onDisconnect}
                            className="text-[12px] font-medium text-red-600 transition-colors hover:text-red-700">
                            {c.account.disconnect}
                        </button>
                    </Section>

                    <Section heading={c.property.heading}>
                        {properties.map((p) => (
                            <div key={p.id} className="rounded-lg border border-gray-200 p-3">
                                <p className="text-[13px] font-medium text-gray-900">{p.name}</p>
                                <div className="mt-1.5 space-y-1">
                                    {/* The number support asks for — permanently visible. */}
                                    <Row label={c.property.id} value={<span className="font-mono">{p.propertyId}</span>} />
                                    <Row label={c.property.account} value={accountNameFor(p.id)} />
                                    <Row label={c.property.timeZone} value={p.timeZone} />
                                    <Row label={c.property.currency} value={p.currencyCode} />
                                </div>
                            </div>
                        ))}
                        <p className="pt-1 text-[11.5px] text-gray-500">{c.property.timeZoneNote}</p>
                        <button
                            type="button"
                            onClick={onChangeProperties}
                            className="text-[12px] font-medium text-geekblue-600 transition-colors hover:text-geekblue-700">
                            {c.property.change}
                        </button>
                    </Section>

                    <Section heading={c.sync.heading}>
                        <Row label={COPY.sync.popover.lastSync} value={formatDateTime(syncStatus.lastSyncAt)} />
                        <Row label={COPY.sync.popover.nextSync} value={formatDateTime(syncStatus.nextSyncAt)} />
                        {/* Never interchangeable with "last sync". */}
                        <Row label={COPY.sync.popover.dataThrough} value={formatDate(syncStatus.dataThrough)} />
                        <Row label="Frequency" value={`${c.sync.cadence} (${SYNC_INTERVAL_HOURS}h)`} />
                    </Section>

                    <Section heading={c.attribution.heading}>
                        <Row label={c.attribution.model} value={COPY.wizard.step4.modelValue} />
                        <Row
                            label={c.attribution.lookback}
                            value={c.attribution.lookbackValue(setup.lookbackDays)}
                        />
                    </Section>

                    <Section heading={c.conversions.heading}>
                        {setup.selectedEvents.length === 0 ? (
                            <p className="text-[12.5px] text-gray-500">{c.conversions.empty}</p>
                        ) : (
                            setup.selectedEvents.map((name) => {
                                // GA4-only for now: a manual per-event value is only shown
                                // when one was actually entered (estimate route), otherwise
                                // GA4's own revenue is authoritative and we show just the event.
                                const manual = setup.eventValues[name] ?? 0;
                                return (
                                    <Row
                                        key={name}
                                        label={<span className="font-mono">{name}</span>}
                                        value={manual > 0 ? `${symbol}${manual.toLocaleString()}` : ""}
                                    />
                                );
                            })
                        )}
                    </Section>

                    <Section heading={c.pageTypes.heading}>
                        {setup.rules.map((rule) => (
                            <Row
                                key={rule.id}
                                label={rule.type || "—"}
                                value={<span className="font-mono text-[11.5px]">{rule.patterns.join(", ")}</span>}
                            />
                        ))}
                        <Row
                            label={c.pageTypes.other}
                            value={<span className="italic text-gray-500">{c.pageTypes.otherPatterns}</span>}
                        />
                    </Section>
                </div>
            )}
        </SidePanel>
    );
}

export default ConnectionSettingsPanel;
